import React, { useState } from 'react';
import { ArrowLeft, Plus, Minus, CheckCircle, User } from 'lucide-react';
import AutocompleteInput from './AutocompleteInput';
import { PREDEFINED_ITEMS } from '../data/database';

const ProFormaOrderer = ({ attendees, onItemsGenerated, onBack }) => {
  const [currentAttendeeIndex, setCurrentAttendeeIndex] = useState(0);
  const [attendeeOrders, setAttendeeOrders] = useState(
    attendees.reduce((acc, attendee) => {
      acc[attendee] = [];
      return acc;
    }, {})
  );
  const [searchValue, setSearchValue] = useState('');
  const [inputKey, setInputKey] = useState(0);

  const currentAttendee = attendees[currentAttendeeIndex];
  const currentOrder = attendeeOrders[currentAttendee] || [];

  const addItemToOrder = (item) => {
    setAttendeeOrders(prev => ({
      ...prev,
      [currentAttendee]: [...currentOrder, { 
        ...item, 
        id: Date.now() + Math.random(),
        quantity: 1 // Ensure default quantity is 1
      }]
    }));
    // Clear the search input and force component reset
    setSearchValue('');
    setInputKey(prev => prev + 1);
  };

  const removeItemFromOrder = (itemId) => {
    setAttendeeOrders(prev => ({
      ...prev,
      [currentAttendee]: currentOrder.filter(item => item.id !== itemId)
    }));
  };

  const updateItemQuantity = (itemId, change) => {
    setAttendeeOrders(prev => ({
      ...prev,
      [currentAttendee]: currentOrder.map(item => {
        if (item.id === itemId) {
          const currentQuantity = parseInt(item.quantity) || 1;
          const newQuantity = Math.max(1, currentQuantity + change);
          return { ...item, quantity: newQuantity };
        }
        return item;
      })
    }));
  };

  const getOrderTotal = (order) => {
    return order.reduce((total, item) => {
      const price = parseFloat(item.price) || 0;
      const quantity = parseInt(item.quantity) || 0;
      return total + (price * quantity);
    }, 0);
  };

  const generateBillItems = () => {
    const itemMap = new Map();

    // Collect all items from all attendees
    Object.entries(attendeeOrders).forEach(([attendee, order]) => {
      order.forEach(item => {
        const key = `${item.name}-${item.price}`;
        if (itemMap.has(key)) {
          const quantity = parseInt(item.quantity) || 0;
          itemMap.get(key).quantity += quantity;
          itemMap.get(key).allocations[attendee] = (itemMap.get(key).allocations[attendee] || 0) + quantity;
        } else {
          const price = parseFloat(item.price) || 0;
          const quantity = parseInt(item.quantity) || 0;
          itemMap.set(key, {
            id: Date.now() + Math.random(),
            name: item.name,
            price: price,
            quantity: quantity,
            total: price * quantity,
            allocations: { [attendee]: quantity }
          });
        }
      });
    });

    return Array.from(itemMap.values());
  };

  const handleContinue = () => {
    const billItems = generateBillItems();
    onItemsGenerated(billItems);
  };

  const isAllAttendeesOrdered = () => {
    return attendees.every(attendee => attendeeOrders[attendee] && attendeeOrders[attendee].length > 0);
  };

  const nextAttendee = () => {
    if (currentAttendeeIndex < attendees.length - 1) {
      setCurrentAttendeeIndex(currentAttendeeIndex + 1);
      setSearchValue(''); // Clear search when switching attendees
      setInputKey(prev => prev + 1); // Force component reset
    }
  };

  const prevAttendee = () => {
    if (currentAttendeeIndex > 0) {
      setCurrentAttendeeIndex(currentAttendeeIndex - 1);
      setSearchValue(''); // Clear search when switching attendees
      setInputKey(prev => prev + 1); // Force component reset
    }
  };

  const goToAttendee = (index) => {
    if (index >= 0 && index < attendees.length) {
      setCurrentAttendeeIndex(index);
      setSearchValue('');
      setInputKey(prev => prev + 1);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Pro Forma Ordering</h2>
        <p className="text-gray-600">Select items for each person</p>
      </div>

      {/* Progress indicator */}
      <div className="flex justify-center">
        <div className="flex items-center space-x-2">
          {attendees.map((attendee, index) => (
            <div key={attendee} className="flex items-center">
              <div className={`
                w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                ${index <= currentAttendeeIndex 
                  ? 'bg-primary-600 text-white' 
                  : 'bg-gray-200 text-gray-600'
                }
              `}>
                {index + 1}
              </div>
              <span className={`ml-2 text-sm ${
                index <= currentAttendeeIndex ? 'text-primary-600 font-medium' : 'text-gray-500'
              }`}>
                {attendee}
              </span>
              {index < attendees.length - 1 && (
                <div className={`w-8 h-0.5 mx-2 ${
                  index < currentAttendeeIndex ? 'bg-primary-600' : 'bg-gray-200'
                }`} />
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="card max-w-4xl mx-auto">
        {/* Current attendee section */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <User className="h-6 w-6 text-primary-600" />
              <h3 className="text-xl font-medium text-gray-900">
                {currentAttendee}'s Order
              </h3>
              <span className="text-sm text-gray-500">
                ({currentAttendeeIndex + 1} of {attendees.length})
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <label htmlFor="proforma-attendee-select" className="text-sm text-gray-600">Jump to:</label>
              <select
                id="proforma-attendee-select"
                value={currentAttendeeIndex}
                onChange={(e) => goToAttendee(Number(e.target.value))}
                className="border border-gray-300 rounded-md px-2 py-1 text-sm bg-white"
              >
                {attendees.map((attendee, index) => (
                  <option key={attendee} value={index}>{attendee}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Add item section */}
          <div className="mb-4">
            <AutocompleteInput
              key={inputKey}
              value={searchValue}
              onChange={setSearchValue}
              onSelect={(selectedItem) => {
                if (selectedItem && selectedItem.name) {
                  addItemToOrder(selectedItem);
                }
              }}
              options={PREDEFINED_ITEMS}
              placeholder="Search and add items..."
              className="w-full"
              allowCustom={true}
              renderOption={(option) => (
                <div className="flex justify-between items-center">
                  <span>{option.name}</span>
                  <span className="text-sm text-gray-500">€{option.price.toFixed(2)}</span>
                </div>
              )}
            />
          </div>

          {/* Current order items */}
          {currentOrder.length > 0 ? (
            <div className="space-y-2">
              <h4 className="font-medium text-gray-900">Current Order:</h4>
              <div className="grid grid-cols-1 gap-2">
                {currentOrder.map((item) => (
                  <div key={item.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-sm">
                    <div className="flex-1 min-w-0">
                      <span className="font-medium text-gray-900 truncate">{item.name}</span>
                      <span className="text-xs text-gray-500 ml-2">€{(parseFloat(item.price) || 0).toFixed(2)} each</span>
                    </div>
                    
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => updateItemQuantity(item.id, -1)}
                        className="p-1 rounded-full bg-white border border-gray-300 hover:bg-gray-50"
                      >
                        <Minus className="h-3 w-3 text-gray-600" />
                      </button>
                      <span className="w-6 text-center font-medium text-xs">{item.quantity}</span>
                      <button
                        onClick={() => updateItemQuantity(item.id, 1)}
                        className="p-1 rounded-full bg-white border border-gray-300 hover:bg-gray-50"
                      >
                        <Plus className="h-3 w-3 text-gray-600" />
                      </button>
                    </div>
                    
                    <div className="ml-2 text-right min-w-0">
                      <span className="font-medium text-gray-900 text-sm">
                        €{((parseFloat(item.price) || 0) * (parseInt(item.quantity) || 0)).toFixed(2)}
                      </span>
                    </div>
                    
                    <button
                      onClick={() => removeItemFromOrder(item.id)}
                      className="ml-2 p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Minus className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
              
              <div className="pt-2 border-t">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-900">Subtotal:</span>
                  <span className="text-lg font-bold text-primary-600">
                    €{getOrderTotal(currentOrder).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p>No items added yet. Search above to add items to {currentAttendee}'s order.</p>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between pt-6 border-t">
          <button
            onClick={onBack}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to attendees</span>
          </button>

          <div className="flex items-center space-x-3">
            {currentAttendeeIndex > 0 && (
              <button
                onClick={prevAttendee}
                className="btn-secondary"
              >
                Previous
              </button>
            )}
            
            {currentAttendeeIndex < attendees.length - 1 ? (
              <button
                onClick={nextAttendee}
                className="btn-primary"
                disabled={currentOrder.length === 0}
              >
                Next Person
              </button>
            ) : (
              <button
                onClick={handleContinue}
                className="btn-primary flex items-center space-x-2"
                disabled={!isAllAttendeesOrdered()}
              >
                <CheckCircle className="h-4 w-4" />
                <span>Generate Pro Forma Bill</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="text-center">
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 max-w-2xl mx-auto">
          <h3 className="font-medium text-green-900 mb-2">Pro Forma Summary</h3>
          <div className="text-sm text-green-700 space-y-1">
            {attendees.map(attendee => {
              const order = attendeeOrders[attendee] || [];
              const total = getOrderTotal(order);
              return (
                <div key={attendee} className="flex justify-between">
                  <span>{attendee}:</span>
                  <span>{order.length} items - €{total.toFixed(2)}</span>
                </div>
              );
            })}
            <div className="pt-2 border-t border-green-300 font-medium">
              <div className="flex justify-between">
                <span>Total:</span>
                <span>€{attendees.reduce((sum, attendee) => sum + getOrderTotal(attendeeOrders[attendee] || []), 0).toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProFormaOrderer;
